/* eslint-disable mobx/missing-observer */
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Empty,
  Modal,
  Space,
  Table,
  Tag,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';

import { useApiClient } from 'app/common/api/api-client.provider';
import { HttpResource } from 'app/common/http-resource';
import { createJsonComponent } from 'app/libs/json-components/create-json-component';
import { WagonDataProvider } from 'app/libs/json-components/wagon-data-provider';
import { appRoute } from 'app/railways/router/routes';

import {
  ESeatBookingType,
  ESeatStatus,
  ESeatType,
  TWagon,
  TWagonSeat,
} from '../../core/trips';
import { getWagonRequest } from '../../requests/get-wagon.request';

import styles from './wagon-scheme.module.css';

type HightlightedSeats = {
  [wagonNum: number]: number[];
};

// type SeatRow = TWagonSeat & { id: string };
export type SeatRow = Omit<
  TWagonSeat,
  'tripId' | 'bookingType' | 'orderId' | 'bookingExpDate'
> & {
  id: string;
  bookingType?: ESeatBookingType;
  orderId?: number | null;
  bookingExpDate?: string | null;
};

const checkBooked = (seat: SeatRow) =>
  seat.status === ESeatStatus.booked &&
  new Date(seat?.bookingExpDate || 0).getTime() > Date.now();

const checkBookedExpired = (seat: SeatRow) =>
  seat.status === ESeatStatus.booked &&
  new Date(seat.bookingExpDate || 0).getTime() < Date.now();

const checkForAdminOpenToSell = (seat: SeatRow) =>
  checkBooked(seat); /*&& checkAdminOrOffice(seat)*/

const checkForAdminBooking = (seat: SeatRow) => {
  const isBookedExpired = checkBookedExpired(seat);

  return seat.status === ESeatStatus.free || isBookedExpired;
};

const filterOpenToSell = (seats: SeatRow[]) => {
  return seats.filter((seat) => checkForAdminOpenToSell(seat));
};

const filterForAdminBookingAvailable = (seats: SeatRow[]) => {
  return seats.filter((seat) => checkForAdminBooking(seat));
};

const OpenSellButton = ({
  seats,
  onChange,
}: {
  seats: SeatRow[];
  onChange?: (seats: SeatRow[]) => void;
}) => {
  const filteredSeats = useMemo(() => filterOpenToSell(seats), [seats]);
  const handleOpenSell = () => {
    const filteredSeats = filterOpenToSell(seats);

    if (seats.length !== filteredSeats.length) {
      Modal.confirm({
        title: 'Открыть продажу?',
        content: (
          <div>
            Среди выбранных билетов есть позиции, которые не могут быть открыты
            к продаже. Они будут проигнорированны и не изменят свой статус.
            <br />
            Открытие к продаже доступно для билетов проданных в кассах вокзалов
            и забронированных администратором.
          </div>
        ),
        okText: 'Продолжить',
        onOk() {
          // const filteredSeats = filterOpenToSell(seats);
          const updatedSeats = filteredSeats.map((seat) => ({
            ...seat,
            bookingType: ESeatBookingType.admin,
            status: ESeatStatus.free,
            bookingExpDate: null,
          }));

          onChange?.(updatedSeats);
        },
      });

      return;
    }

    const updatedSeats = filteredSeats.map((seat) => ({
      ...seat,
      bookingType: ESeatBookingType.admin,
      status: ESeatStatus.free,
      bookingExpDate: null,
    }));

    onChange?.(updatedSeats);
  };
  // const isFree =
  //   record.status === ESeatStatus.free ||
  //   (record.status === ESeatStatus.booked &&
  //     new Date(record.bookingExpDate || 0).getTime() < Date.now());

  // const isAvailableForSell =
  //   record.bookingType === ESeatBookingType.admin ||
  //   record.bookingType === ESeatBookingType.office;

  // const isDisabled =

  return (
    <Button disabled={filteredSeats.length === 0} onClick={handleOpenSell}>
      Открыть продажу
    </Button>
  );
};

const BookingSelectedButton = ({
  seats,
  onChange,
}: {
  seats: SeatRow[];
  onChange?: (seats: SeatRow[]) => void;
}) => {
  const filteredSeats = useMemo(
    () => filterForAdminBookingAvailable(seats),
    [seats],
  );

  const handleOpenSell = () => {
    const filteredSeats = filterForAdminBookingAvailable(seats);

    if (seats.length !== filteredSeats.length) {
      Modal.confirm({
        title: 'Забронировать места?',
        content: (
          <div>
            Среди выбранных мест есть позиции, которые не могут быть
            забронированы. Они будут проигнорированны и не изменят свой статус.
            <br />
            Бронирование мест доступно для позиций свободных на данный момент.
          </div>
        ),
        okText: 'Продолжить',
        onOk() {
          const updatedSeats = filteredSeats.map((seat) => ({
            ...seat,
            bookingType: ESeatBookingType.admin,
            status: ESeatStatus.booked,
            bookingExpDate: new Date(
              Date.now() + 1 * 365 * 24 * 60 * 60 * 1000,
            ).toJSON(),
          }));

          onChange?.(updatedSeats);
        },
      });

      return;
    }

    const updatedSeats = filteredSeats.map((seat) => ({
      ...seat,
      bookingType: ESeatBookingType.admin,
      status: ESeatStatus.booked,
      bookingExpDate: new Date(
        Date.now() + 1 * 365 * 24 * 60 * 60 * 1000,
      ).toJSON(),
    }));

    onChange?.(updatedSeats);
  };

  return (
    <Button
      disabled={filteredSeats.length === 0}
      style={{
        backgroundColor:
          filteredSeats.length === 0 ? '' : 'rgb(255 191 0 / 64%)',
      }}
      onClick={handleOpenSell}
    >
      Забронировать
    </Button>
  );
};

export const WagonScheme = observer(
  ({
    wagons,
    onChange,
  }: {
    wagons: TWagon[];
    onChange?: (seats: SeatRow[]) => void;
  }) => {
    const [hightlightedSeats, setHightlightedSeats] =
      useState<HightlightedSeats>({});

    const [selectedRows, setSelectedRows] = useState<SeatRow[]>([]);

    const handleClearHighlightBySelection = () => {
      selectedRows.forEach((row) => {
        setHightlightedSeats((prev) => ({
          ...prev,
          [row.wagonNum]: prev[row.wagonNum].filter((s) => s !== row.seatNum),
        }));
      });

      setSelectedRows([]);
    };

    const handleSeatsChange = (seats: {
      wagonNum: number;
      seats: number[];
    }) => {
      setHightlightedSeats((prev) => ({
        ...prev,
        [seats.wagonNum]: seats.seats,
      }));
    };

    const seatList = useMemo(() => {
      const seatsEntries = Object.entries(hightlightedSeats);
      const seatList: SeatRow[] = [];

      seatsEntries.forEach(([wagonNum, seats]) => {
        const foundWagon = wagons.find(
          (wag) => wag.number === Number(wagonNum),
        );

        seats.forEach((seat) => {
          const foundSeat = foundWagon?.seats.find((s) => s.seatNum === seat);

          if (!foundSeat) {
            seatList.push({
              type: ESeatType.normal,
              status: ESeatStatus.free,
              id: `${wagonNum}-${seat}`,
              wagonNum: Number(wagonNum),
              seatNum: seat,
            });

            return;
          }

          seatList.push({
            ...foundSeat,
            id: `${wagonNum}-${seat}`,
            wagonNum: Number(wagonNum),
            seatNum: seat,
          });
        });
      });

      return seatList;
    }, [hightlightedSeats, wagons]);

    useEffect(() => {
      // update selected rows, when they state changes
      setSelectedRows((selectedRows) =>
        selectedRows.map((selectedRow) => {
          const foundSeat = seatList.find((seat) => seat.id === selectedRow.id);
          return foundSeat ? { ...foundSeat } : selectedRow;
        }),
      );
    }, [seatList, selectedRows]);

    return (
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Collapse bordered={false} style={{ background: '#fff' }}>
            <Collapse.Panel key="1" header="Справка">
              <div className={styles.legend}>
                <p className={styles.wagon}>
                  <button className={styles.legendBtn}>42</button>
                  <span style={{ fontSize: 14 }}> - свободное</span>
                </p>

                <p className={styles.wagon}>
                  <button className={styles.legendBtn} data-selected="true">
                    42
                  </button>{' '}
                  <span style={{ fontSize: 14 }}>- выбранное</span>
                </p>

                <p className={styles.wagon}>
                  <button
                    className={styles.legendBtn}
                    data-booking-type="1"
                    data-booking-available="false"
                  >
                    42
                  </button>{' '}
                  <span style={{ fontSize: 14 }}>
                    - продажа на кассе вокзала
                  </span>
                </p>
                <p className={styles.wagon}>
                  <button
                    className={styles.legendBtn}
                    data-booking-type="2"
                    data-booking-available="false"
                  >
                    42
                  </button>{' '}
                  <span style={{ fontSize: 14 }}>
                    - продажа через приложение
                  </span>
                </p>
                <p className={styles.wagon}>
                  <button
                    className={styles.legendBtn}
                    data-booking-type="3"
                    data-booking-available="false"
                  >
                    42
                  </button>{' '}
                  <span style={{ fontSize: 14 }}>
                    - забронировано администратором
                  </span>
                </p>
                <p className={styles.wagon}>
                  <button
                    className={styles.legendBtn}
                    data-booking-type="0"
                    data-booking-available="false"
                  >
                    42
                  </button>{' '}
                  <span style={{ fontSize: 14 }}>
                    - канал продажи не известен *
                  </span>
                  <p style={{ fontSize: 14, color: 'gray', marginTop: 8 }}>
                    * — старые билеты, купленные или забронированые до появления
                    схемы вагонов на сайте.
                  </p>
                </p>
              </div>
              <Divider>Изменение статуса мест</Divider>
              <div style={{ maxWidth: 320 }}>
                <p style={{ marginBottom: 8 }}>
                  Выпуск мест в продажу возможен для билетов забронированных в
                  кассах вокзалов, забронированных через приложение (WEB) или
                  забронированных администратором.
                </p>
                <p style={{ marginBottom: 8 }}>
                  Для возрата билетов купленнных через приложение (WEB)
                  необходимо перейти в данные заказа.
                </p>
                <p style={{ marginBottom: 8 }}>
                  Бронирование мест администратором доступно только для
                  свободных позиций
                </p>
              </div>
              <Divider>Выбор нескольких мест</Divider>
              <div>
                <p>
                  Для выбора диапазона кликните по первому месту, затем,
                  удерживая клавишу <Tag>Shift</Tag> кликните по последнему
                  месту.
                </p>
                <p>Места выбираются в порядке слева направо, сверху вниз.</p>
              </div>
            </Collapse.Panel>
          </Collapse>

          {wagons.map((wagon) => (
            <Wagon
              value={{
                wagonNum: wagon.number,
                seats: hightlightedSeats[wagon.number] || [],
              }}
              key={wagon.number}
              disabled={false}
              data={wagon}
              onChange={handleSeatsChange}
            />
          ))}
        </div>

        <Card title="Выбранные места" size="small" style={{ width: '100%' }}>
          <div
            style={{
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              disabled={selectedRows.length === 0}
              onClick={handleClearHighlightBySelection}
            >
              Отменить выбор
            </Button>

            <Space>
              <OpenSellButton
                seats={selectedRows}
                onChange={(seats) => {
                  onChange?.(seats);
                  // setSelectedRows([]);
                }}
              />

              <BookingSelectedButton
                seats={selectedRows}
                onChange={(seats) => {
                  onChange?.(seats);
                  // setSelectedRows([]);
                }}
              />
            </Space>
          </div>

          <Table
            rowKey="id"
            dataSource={seatList}
            size="small"
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedRows.map((r) => r.id),
              onChange: (_, rowsData) => setSelectedRows(rowsData),
            }}
            locale={{
              emptyText: (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  Выберите места на схеме слева
                </Empty>
              ),
            }}
            bordered={true}
            pagination={false}
            columns={[
              {
                title: 'Место',
                dataIndex: 'seatNum',
                width: '7%',
                sorter: (a, b) => a.seatNum - b.seatNum,
              },
              {
                title: 'Вагон',
                dataIndex: 'wagonNum',
                width: '7%',
                sorter: (a, b) => a.wagonNum - b.wagonNum,
              },

              {
                title: 'Последнее бронирование',
                dataIndex: 'bookingType',
                sorter: (a, b) => a.bookingType || 1 - (b.bookingType || 0),
                width: '20%',
                render: (_, record) => {
                  if (record.bookingType === ESeatBookingType.office)
                    return <Badge color="#5b9cd5" text="Касса вокзала" />;
                  if (record.bookingType === ESeatBookingType.web)
                    return <Badge color="#f639d3" text="Приложение" />;
                  if (record.bookingType === ESeatBookingType.admin)
                    return <Badge color="#ffbf00" text="Администратор" />;
                },
              },
              {
                title: 'Статус',
                dataIndex: 'status',
                width: '18%',
                sorter: (a, b) => a.status - b.status,
                render: (_, record) => {
                  if (
                    record.status === ESeatStatus.free ||
                    checkBookedExpired(record)
                  )
                    return <Badge status="default" text="Свободно" />;
                  if (checkBooked(record))
                    return <Badge status="processing" text="Забронировано" />;
                  if (record.status === ESeatStatus.sold)
                    return <Badge status="success" text="Продано" />;
                },
              },
              {
                title: 'ID заказа',
                dataIndex: 'orderId',
                width: '13%',
                render: (_, record) => {
                  if (!record.orderId) return null;

                  const path = appRoute.TICKETS.ORDER.buildPath({
                    id: record.orderId,
                  });

                  return <Link to={path}>{record.orderId}</Link>;
                },
              },
              {
                title: 'Действия',
                width: '80%',
                render: (_, record) => {
                  const isOpenToSell = checkForAdminOpenToSell(record);
                  const isAdminBookinAvailable = checkForAdminBooking(record);

                  const changeToSell = () => {
                    const changedSeat = {
                      ...record,
                      bookingType: ESeatBookingType.admin,
                      status: ESeatStatus.free,
                      bookingExpDate: null,
                    };

                    onChange?.([changedSeat]);
                  };

                  const changeToBook = () => {
                    const changedSeat = {
                      ...record,
                      bookingType: ESeatBookingType.admin,
                      status: ESeatStatus.booked,
                      bookingExpDate: new Date(
                        Date.now() + 1 * 365 * 24 * 60 * 60 * 1000,
                      ).toJSON(),
                    };

                    onChange?.([changedSeat]);
                  };

                  return (
                    <Space>
                      {isAdminBookinAvailable && (
                        <Button
                          size="small"
                          style={{ background: 'rgb(255 191 0 / 64%)' }}
                          onClick={changeToBook}
                        >
                          Забронировать
                        </Button>
                      )}

                      {isOpenToSell && (
                        <Button size="small" onClick={changeToSell}>
                          Открыть продажу
                        </Button>
                      )}
                    </Space>
                  );
                },
              },
            ]}
          />
        </Card>
        {/* </Affix> */}
      </div>
    );
  },
);

type WagonProps = {
  data: TWagon;
  disabled?: boolean;
  value: { wagonNum: number; seats: number[] };
  onChange?: (seats: { wagonNum: number; seats: number[] }) => void;
};
export const Wagon = observer(
  ({ data, disabled, value, onChange }: WagonProps) => {
    const apiClient = useApiClient();
    const direction = data.direction ?? 'forward';
    const [wagonSchemeJson, setWagonShemeJson] = useState(null);

    const [getWagon] = useState(
      () =>
        new HttpResource<number, any>({
          apiClient,
          httpRequest: getWagonRequest,
          // errorNotify: new AppNotify(),
          onFinish(result: any) {
            setWagonShemeJson(result);
          },
        }),
    );
    const handleOnChange = (seats: number[]) => {
      onChange && onChange({ wagonNum: data.number, seats });
    };

    useEffect(() => {
      getWagon.send(data.typeId);
    }, []);

    const title = `Вагон № ${data.number}`;

    return (
      <div>
        <Card size="small" title={title} style={{ marginTop: 16 }}>
          {!getWagon.isLoading && (
            <WagonPainter
              value={value.seats}
              disabled={disabled}
              scheme={wagonSchemeJson}
              direction={direction}
              seats={data.seats}
              onChange={handleOnChange}
            />
          )}
        </Card>
      </div>
    );
  },
);

type WagonPainterProps = {
  scheme: any | null;
  direction: 'forward' | 'backward';
  seats: TWagon['seats'];
  disabled?: boolean;
  value: number[];
  onChange?: (seats: number[]) => void;
};
// eslint-disable-next-line mobx/missing-observer

const seatExtraDataAttrsBuilder = (seat: {
  status: ESeatStatus;
  bookingType?: ESeatBookingType;
  bookingExpDate?: string | null;
}) => {
  const isAvailable =
    seat.status === ESeatStatus.free ||
    (seat.status === ESeatStatus.booked &&
      new Date(seat.bookingExpDate || 0).getTime() < Date.now());

  return {
    'data-booking-type': seat.bookingType,
    'data-booking-available': isAvailable,
  } as const;
};

const WagonPainter = ({
  scheme,
  direction,
  seats,
  disabled = false,
  value,
  onChange,
}: WagonPainterProps) => {
  const WagonComponent = useMemo(() => {
    if (!scheme) return () => null;

    return () => createJsonComponent(scheme.schema);
  }, [scheme]);

  return (
    <WagonDataProvider
      direction={direction}
      scheme={scheme}
      seats={seats}
      disabled={disabled}
      seatExtraDataAttrsBuilder={seatExtraDataAttrsBuilder}
      value={value}
      onChange={onChange}
    >
      <div className={styles.wagon}>
        <WagonComponent />
      </div>
    </WagonDataProvider>
  );
};
